import { useState, useEffect } from 'react';
import { adminAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Loader2, Plus, Edit, CreditCard, UserPlus, XCircle, DollarSign } from 'lucide-react';
import { AdminPageShell } from '../components/AdminShared';

export default function AdminPayRatesPage() {
  const [schedules, setSchedules] = useState([]);
  const [interpreters, setInterpreters] = useState([]);
  const [loading, setLoading] = useState(true);
  // Create/Edit
  const [showDialog, setShowDialog] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', rate_cents_per_minute: 100, min_call_minutes: 2, min_call_minutes_label: 'Minimum Call Duration (minutes)' });
  const [selectedInterps, setSelectedInterps] = useState([]);
  // Detail view
  const [showDetail, setShowDetail] = useState(null);
  const [detailData, setDetailData] = useState(null);
  // Override
  const [showOverride, setShowOverride] = useState(null);
  const [overrideRate, setOverrideRate] = useState(0);
  // Add interpreters to schedule
  const [showAddInterps, setShowAddInterps] = useState(null);
  const [addInterps, setAddInterps] = useState([]);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [sRes, iRes] = await Promise.all([adminAPI.listPaySchedules(), adminAPI.listInterpreters({})]);
      setSchedules(sRes.data); setInterpreters(iRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const openCreate = () => {
    setEditId(null);
    setForm({ name: '', rate_cents_per_minute: 100, min_call_minutes: 2, min_call_minutes_label: 'Minimum Call Duration (minutes)' });
    setSelectedInterps([]);
    setShowDialog(true);
  };

  const openEdit = (s) => {
    setEditId(s.id);
    setForm({ name: s.name, rate_cents_per_minute: s.rate_cents_per_minute, min_call_minutes: s.min_call_minutes, min_call_minutes_label: s.min_call_minutes_label || 'Minimum Call Duration (minutes)' });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    try {
      if (editId) {
        await adminAPI.updatePaySchedule(editId, form);
      } else {
        await adminAPI.createPaySchedule({ ...form, interpreter_ids: selectedInterps });
      }
      setShowDialog(false); load();
    } catch (e) { console.error(e); }
  };

  const loadDetail = async (id) => {
    setShowDetail(id);
    try { const res = await adminAPI.getPaySchedule(id); setDetailData(res.data); } catch (e) { console.error(e); }
  };

  const handleRemoveInterpreter = async (scheduleId, interpId) => {
    try { await adminAPI.removeInterpreterFromSchedule(scheduleId, { interpreter_id: interpId }); loadDetail(scheduleId); load(); } catch (e) { console.error(e); }
  };

  const handleOverride = async () => {
    try { await adminAPI.overrideInterpreterPay({ interpreter_id: showOverride, rate_cents_per_minute: overrideRate }); setShowOverride(null); load(); } catch (e) { console.error(e); }
  };

  const handleAddInterpreters = async () => {
    if (!showAddInterps || addInterps.length === 0) return;
    try { await adminAPI.addInterpretersToSchedule(showAddInterps, { interpreter_ids: addInterps }); setShowAddInterps(null); setAddInterps([]); loadDetail(showAddInterps); load(); } catch (e) { console.error(e); }
  };

  const toggleInterp = (id) => {
    setSelectedInterps(p => p.includes(id) ? p.filter(i => i !== id) : [...p, id]);
  };
  const toggleAddInterp = (id) => {
    setAddInterps(p => p.includes(id) ? p.filter(i => i !== id) : [...p, id]);
  };

  if (loading) return <AdminPageShell loading={true} />;

  return (
    <AdminPageShell>
      <div className="p-6 md:p-8 max-w-6xl animate-fade-in" data-testid="admin-pay-rates-page">
        <div className="flex items-center justify-between mb-8">
          <div><h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'DM Sans' }}>Pay Rates</h1>
            <p className="text-muted-foreground mt-1 text-sm">Manage interpreter pay schedules and individual rate overrides</p></div>
          <Button onClick={openCreate} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="create-pay-schedule-button"><Plus className="w-4 h-4 mr-2" />New Pay Schedule</Button>
        </div>

        {/* Schedules Table */}
        <div className="rounded-lg border bg-card mb-8">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Schedule Name</TableHead><TableHead>Rate/Min</TableHead><TableHead>Min Call</TableHead>
              <TableHead>Interpreters</TableHead><TableHead>Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {schedules.map(s => (
                <TableRow key={s.id} className="cursor-pointer hover:bg-accent/50" onClick={() => loadDetail(s.id)} data-testid={`pay-row-${s.id}`}>
                  <TableCell className="font-medium">{s.name}</TableCell>
                  <TableCell className="font-mono text-sm">${(s.rate_cents_per_minute / 100).toFixed(2)}/min</TableCell>
                  <TableCell className="font-mono text-sm">{s.min_call_minutes} min</TableCell>
                  <TableCell><Badge variant="secondary" className="text-xs">{s.interpreter_count || 0} interpreters</Badge></TableCell>
                  <TableCell>
                    <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(s)} data-testid={`edit-pay-${s.id}`}><Edit className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => { setShowAddInterps(s.id); setAddInterps([]); }} data-testid={`add-interps-${s.id}`}><UserPlus className="w-4 h-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {schedules.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No pay schedules</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>

        {/* Individual Interpreter Rates */}
        <div className="rounded-lg border bg-card">
          <div className="p-4 border-b"><h3 className="font-semibold text-sm" style={{ fontFamily: 'DM Sans' }}>Individual Interpreter Rates</h3></div>
          <ScrollArea className="max-h-[400px]">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>Pay Rate</TableHead><TableHead>Schedule</TableHead><TableHead>Override</TableHead><TableHead>Action</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {interpreters.filter(i => i.approval_status === 'approved').map(i => (
                  <TableRow key={i.id} data-testid={`interp-pay-row-${i.id}`}>
                    <TableCell className="font-medium text-sm">{i.name}</TableCell>
                    <TableCell className="font-mono text-sm">{i.profile?.pay_rate_cents_per_minute ? `$${(i.profile.pay_rate_cents_per_minute / 100).toFixed(2)}/min` : '-'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{i.profile?.pay_schedule_id ? schedules.find(s => s.id === i.profile.pay_schedule_id)?.name || i.profile.pay_schedule_id : '-'}</TableCell>
                    <TableCell>{i.profile?.pay_rate_override ? <Badge variant="secondary" className="text-xs">Override</Badge> : null}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => { setShowOverride(i.id); setOverrideRate(i.profile?.pay_rate_cents_per_minute || 100); }} data-testid={`override-pay-${i.id}`}>
                        <DollarSign className="w-3.5 h-3.5 mr-1" />Edit Rate
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </div>

        {/* Create/Edit Pay Schedule Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="pay-schedule-dialog">
            <DialogHeader><DialogTitle>{editId ? 'Edit Pay Schedule' : 'Create Pay Schedule'}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2"><Label>Schedule Name *</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} data-testid="pay-name" placeholder="Standard Pay" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Rate (cents/min) *</Label><Input type="number" value={form.rate_cents_per_minute} onChange={e => setForm(p => ({ ...p, rate_cents_per_minute: parseInt(e.target.value) || 0 }))} data-testid="pay-rate" /></div>
                <div className="space-y-2"><Label>{form.min_call_minutes_label}</Label><Input type="number" value={form.min_call_minutes} onChange={e => setForm(p => ({ ...p, min_call_minutes: parseInt(e.target.value) || 0 }))} data-testid="pay-min-call" /></div>
              </div>
              <div className="space-y-2"><Label>Min Call Label</Label><Input value={form.min_call_minutes_label} onChange={e => setForm(p => ({ ...p, min_call_minutes_label: e.target.value }))} data-testid="pay-min-call-label" /></div>
              {!editId && (
                <div className="space-y-2">
                  <Label>Assign Interpreters</Label>
                  <ScrollArea className="max-h-48 border rounded-md p-3">
                    {interpreters.filter(i => i.approval_status === 'approved').map(i => (
                      <label key={i.id} className="flex items-center gap-2 py-1 cursor-pointer">
                        <Checkbox checked={selectedInterps.includes(i.id)} onCheckedChange={() => toggleInterp(i.id)} />
                        <span className="text-sm">{i.name}</span>
                      </label>
                    ))}
                  </ScrollArea>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={!form.name} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="submit-pay-schedule">{editId ? 'Save' : 'Create'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail Dialog */}
        <Dialog open={!!showDetail} onOpenChange={() => { setShowDetail(null); setDetailData(null); }}>
          <DialogContent className="max-w-lg" data-testid="pay-detail-dialog">
            {detailData ? (
              <>
                <DialogHeader><DialogTitle>{detailData.name}</DialogTitle>
                  <DialogDescription>Rate: ${(detailData.rate_cents_per_minute / 100).toFixed(2)}/min | Min call: {detailData.min_call_minutes} min</DialogDescription></DialogHeader>
                <div className="py-2">
                  <h4 className="text-sm font-semibold mb-2">Assigned Interpreters ({detailData.interpreters?.length || 0})</h4>
                  {(detailData.interpreters || []).length === 0 ? <p className="text-sm text-muted-foreground">No interpreters assigned</p> : (
                    <div className="space-y-2">
                      {detailData.interpreters.map(interp => (
                        <div key={interp.user_id} className="flex items-center justify-between p-2 rounded-md bg-secondary/50">
                          <span className="text-sm">{interp.name}</span>
                          <Button variant="ghost" size="icon" onClick={() => handleRemoveInterpreter(showDetail, interp.user_id)} className="h-7 w-7 text-destructive">
                            <XCircle className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>}
          </DialogContent>
        </Dialog>

        {/* Add Interpreters to Schedule */}
        <Dialog open={!!showAddInterps} onOpenChange={() => setShowAddInterps(null)}>
          <DialogContent data-testid="add-interps-dialog">
            <DialogHeader><DialogTitle>Add Interpreters to Schedule</DialogTitle></DialogHeader>
            <ScrollArea className="max-h-72 border rounded-md p-3 my-2">
              {interpreters.filter(i => i.approval_status === 'approved').map(i => (
                <label key={i.id} className="flex items-center gap-2 py-1 cursor-pointer">
                  <Checkbox checked={addInterps.includes(i.id)} onCheckedChange={() => toggleAddInterp(i.id)} />
                  <span className="text-sm">{i.name}</span>
                  <span className="text-xs text-muted-foreground ml-auto">{i.profile?.pay_rate_cents_per_minute ? `$${(i.profile.pay_rate_cents_per_minute / 100).toFixed(2)}/min` : 'No rate'}</span>
                </label>
              ))}
            </ScrollArea>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddInterps(null)}>Cancel</Button>
              <Button onClick={handleAddInterpreters} disabled={addInterps.length === 0} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="submit-add-interps">
                Add {addInterps.length} Interpreter{addInterps.length !== 1 ? 's' : ''}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Override Dialog */}
        <Dialog open={!!showOverride} onOpenChange={() => setShowOverride(null)}>
          <DialogContent data-testid="override-dialog">
            <DialogHeader><DialogTitle>Override Pay Rate</DialogTitle>
              <DialogDescription>Set a custom rate for this interpreter that overrides their schedule rate</DialogDescription></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2"><Label>Rate (cents/min)</Label><Input type="number" value={overrideRate} onChange={e => setOverrideRate(parseInt(e.target.value) || 0)} data-testid="override-rate-input" /></div>
              <p className="text-sm text-muted-foreground">= ${(overrideRate / 100).toFixed(2)} per minute</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowOverride(null)}>Cancel</Button>
              <Button onClick={handleOverride} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="submit-override">Save Override</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminPageShell>
  );
}
